import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import { FaChartLine, FaUsers, FaGlobe, FaMoneyBillWave, FaProjectDiagram } from 'react-icons/fa';
import { getPublicHomepageStats } from '../services/siteContentService';
import { useTranslation } from 'react-i18next';
import { usePublicPaths } from '../routes/publicPaths';

const ICON_MAP = {
  projects: FaProjectDiagram,
  partners: FaUsers,
  money: FaMoneyBillWave,
  regions: FaGlobe,
};

const StatCard = ({ title, value, iconKey }) => {
  const [count, setCount] = useState(0);
  const Icon = ICON_MAP[iconKey] || FaProjectDiagram;
  const end = parseInt(String(value).replace(/,/g, ''), 10) || 0;

  useEffect(() => {
    let current = 0;
    const step = Math.max(1, Math.floor(end / 60));
    const timer = setInterval(() => {
      current += step;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 30);
    return () => clearInterval(timer);
  }, [end]);

  return (
    <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <h3 className="text-3xl font-bold mt-2 text-brand-primary">{count.toLocaleString()}</h3>
        </div>
        <div className="p-3 rounded-full bg-brand-surface text-brand-primary">
          <Icon className="text-2xl" />
        </div>
      </div>
    </div>
  );
};

const StatsPage = () => {
  const { t } = useTranslation();
  const paths = usePublicPaths();
  const [summary, setSummary] = useState([]);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    Promise.all([
      getPublicHomepageStats('stats_summary'),
      getPublicHomepageStats('stats_sector'),
    ])
      .then(([summaryData, sectorData]) => {
        if (summaryData?.length) setSummary(summaryData);
        if (sectorData?.length) setSectors(sectorData);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${t('public.statsPage.metaTitle')} | ${t('public.brand.titleSuffix')}`}</title>
      </Helmet>
      <Navbar />
      <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <nav className="flex mb-8">
          <ol className="flex items-center space-x-2 rtl:space-x-reverse">
            <li><Link to={paths.home} className="text-brand-primary hover:text-brand-primary-dark">{t('public.nav.home')}</Link></li>
            <li><span className="text-gray-400 mx-2">/</span></li>
            <li className="text-gray-500">{t('public.statsPage.metaTitle')}</li>
          </ol>
        </nav>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('public.statsPage.title')}</h1>
          <p className="text-lg text-gray-600">{t('public.statsPage.lead')}</p>
        </div>

        {summary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {summary.map((stat) => (
              <StatCard key={stat.id} title={stat.title} value={stat.value} iconKey={stat.icon} />
            ))}
          </div>
        )}

        {sectors.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">
                <FaChartLine className="inline-block ml-2 text-brand-primary" />
                {t('public.statsPage.bySector')}
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('public.statsPage.sector')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('public.statsPage.projectsCount')}</th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('public.statsPage.totalAmounts')}</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sectors.map((sector) => (
                      <tr key={sector.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{sector.title}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{sector.value}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {(sector.extra_data?.amount || 0).toLocaleString()} {t('public.common.currency')}
                        </td>
                        <td className="px-6 py-4 text-right text-2xl">{sector.extra_data?.emoji}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default StatsPage;
